<template>
  <header :class="['nav', { solid: isSolid || showSearch }]">
    <div class="nav-inner">
      <div class="brand">
        <NuxtLink to="/" aria-label="首页">
          <img :src="logoUrl" alt="YOOSTONE" />
        </NuxtLink>
      </div>
      <div class="right">
        <ul v-if="showMenu" class="menu">
          <li
            v-for="m in renderMenus"
            :key="m.to"
            :class="['menu-item', { 'has-submenu': m.categories && m.categories.length > 0 }]"
          >
            <NuxtLink :to="m.to">{{ m.text }}</NuxtLink>
            <ul v-if="m.categories && m.categories.length > 0" class="submenu">
              <li v-for="(c, i) in m.categories" :key="c?.categoryId ?? c?.id ?? i" class="submenu-item">
                <NuxtLink :to="`/goods?categoryId=${encodeURIComponent(c?.categoryId)}`">{{ c?.categoryName }}</NuxtLink>
              </li>
            </ul>
          </li>
        </ul>
        <div class="actions">
          <i class="iconfont icon-sousuo clickable" @click="openSearch"></i>
          <!-- 登录后显示头像和昵称 -->
          <div v-if="isLoggedIn() && userInfo" class="user-info clickable" @click="onUserClick">
            <img v-if="userInfo.pic" :src="userInfo.pic" class="user-avatar" alt="头像" />
            <span v-if="userInfo.nickName" class="user-nickname">{{ userInfo.nickName }}</span>
          </div>
          <!-- 未登录显示个人中心图标 -->
          <i
            v-else
            class="iconfont icon-gerenzhongxin clickable"
            @click="onUserClick"
          ></i>
          <NuxtLink to="/cart" class="cart" aria-label="购物车">
            <i class="iconfont icon-gouwuche"></i>
            <span v-if="cartBadgeCount > 0" class="badge">{{ cartBadgeCount }}</span>
          </NuxtLink>
        </div>
      </div>
    </div>
  </header>
  <div v-if="showSearch" class="search-wrap">
    <div class="search-inner">
      <ElInput
        v-model="query"
        placeholder="请输入搜索词"
        size="large"
        class="s-input"
        @keydown.esc="closeSearch"
        @keydown.enter.prevent="submitSearch"
        autofocus
      >
        <template #prefix>
          <i class="iconfont icon-sousuo s-prefix"></i>
        </template>
        <template #suffix>
          <ElIcon class="clickable s-suffix" @click="closeSearch"><Close /></ElIcon>
        </template>
      </ElInput>
      <ul class="suggest">
        <li v-if="query.trim() && !loadingSug && sug.length === 0" class="empty">暂无关联词</li>
        <li v-for="s in sug" :key="s" @click="select(s)">
          <template v-for="(seg, i) in highlightParts(s, query)" :key="i">
            <span v-if="seg.m" class="hl">{{ seg.t }}</span>
            <span v-else>{{ seg.t }}</span>
          </template>
        </li>
      </ul>
    </div>
  </div>
  <div v-if="showSearch" class="search-mask" @click="closeSearch" />
</template>

<script setup lang="ts">
import { ref, onMounted, computed, onUnmounted, watch } from "vue";
import { useRouter } from "#imports";
import { Search, Close } from "@element-plus/icons-vue";
import { NuxtLink } from "#components";
import logoUrl from "~/assets/image/logo.png";
const api = useApi();
const props = withDefaults(
  defineProps<{
    overlay?: boolean;
    showMenu?: boolean;
    cartCount?: number;
    menus?: { text: string; to: string }[];
  }>(),
  {
    overlay: false,
    showMenu: true,
    cartCount: 0,
    menus: () => [
      { text: "首页", to: "/" },
      { text: "新品", to: "/newList" },
      { text: "资讯", to: "/news" },
      { text: "联系我们", to: "/help/contact" },
    ],
  }
);
type MenuItem = { text: string; to: string; categories?: any[]; catId?: number | string };
const dynamicCategories = ref<{ name: string; id: number | string; categories: any[] }[]>([]);
const renderMenus = computed<MenuItem[]>(() => {
  const cats = dynamicCategories.value;
  const home: MenuItem = { text: "首页", to: "/" };
  // 添加新品菜单项
  const newProducts: MenuItem = { text: "新品", to: "/newList" };
  const head: MenuItem[] = cats.map((cat) => ({
    text: cat.name,
    to: `/goods?categoryId=${encodeURIComponent(cat.id)}`,
    categories: Array.isArray(cat.categories) ? cat.categories : [],
    catId: cat.id,
  }));
  const tail: MenuItem[] = (props.menus || [])
    .filter((m) => m.text === "联系我们" || m.text === "资讯")
    .map((m) => ({ text: m.text, to: m.to }));
  return [home, newProducts, ...head, ...tail];
});
const isSolid = ref(!props.overlay);
const cartBadgeCount = ref<number>(props.cartCount || 0);
const showSearch = ref(false);
const query = ref("");
const sug = ref<string[]>([]);
const loadingSug = ref(false);
let sugTimer: any = null;
let sugStamp = 0;
const fetchSug = async (q: string, stamp: number) => {
  try {
    const r = await api.get<any>("/search/wordList", { keyWord: q }, { server: false, key: `search-wordList-${q}`, retry: 0 });
    const raw = (r as any)?.data ?? (r as any)?.raw;
    const arr = Array.isArray((raw as any)?.records)
      ? (raw as any).records
      : Array.isArray((raw as any)?.list)
      ? (raw as any).list
      : Array.isArray((raw as any)?.data)
      ? (raw as any).data
      : Array.isArray(raw)
      ? (raw as any)
      : [];
    const toText = (x: any) =>
      String(
        x?.word ??
          x?.keyWord ??
          x?.keyword ??
          x?.name ??
          x?.label ??
          x?.text ??
          x?.value ??
          x
      );
    const next = arr
      .map((x: any) => toText(x))
      .map((s: string) => s.trim())
      .filter((s: string) => !!s)
      .slice(0, 10);
    if (stamp === sugStamp) {
      sug.value = next;
    }
  } catch {
    if (stamp === sugStamp) {
      sug.value = [];
    }
  } finally {
    if (stamp === sugStamp) {
      loadingSug.value = false;
    }
  }
};
watch(
  query,
  (val) => {
    clearTimeout(sugTimer);
    const q = String(val || "").trim();
    if (!q) {
      sug.value = [];
      loadingSug.value = false;
      return;
    }
    loadingSug.value = true;
    const stamp = Date.now();
    sugStamp = stamp;
    sugTimer = setTimeout(() => fetchSug(q, stamp), 200);
  },
  { immediate: false }
);
type Seg = { t: string; m: boolean };
const escapeReg = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const highlightParts = (text: string, qRef: string): Seg[] => {
  const q = qRef.trim();
  if (!q) return [{ t: text, m: false }];
  const re = new RegExp(escapeReg(q), "gi");
  const parts: Seg[] = [];
  let last = 0;
  for (const m of text.matchAll(re)) {
    const idx = m.index || 0;
    if (idx > last) parts.push({ t: text.slice(last, idx), m: false });
    parts.push({ t: m[0], m: true });
    last = idx + m[0].length;
  }
  if (last < text.length) parts.push({ t: text.slice(last), m: false });
  return parts;
};
const openSearch = () => {
  showSearch.value = true;
};
const closeSearch = () => {
  showSearch.value = false;
};
const submitSearch = () => {
  const q = String(query.value || "").trim();
  if (!q) return;
  router.push({ path: "/search", query: { keyWord: q } });
  showSearch.value = false;
};
const select = (s: string) => {
  query.value = s;
  submitSearch();
};
const router = useRouter();
const auth = useAuth();
const userInfo = ref<any>(null);

// 获取用户信息
const getUserInfo = () => {
  userInfo.value = auth.getUserInfo();
};

const isLoggedIn = () => {
  return auth.checkLogin();
};
const onUserClick = () => {
  const logged = isLoggedIn();
  router.push(logged ? "/user/profile" : "/login");
};
const fetchCartCount = async () => {
  try {
    const r = await api.post<any>("/p/shopCart/info", {}, { server: false, key: "cart-info", retry: 0 });
    const data = (r as any)?.data ?? (r as any)?.raw ?? {};
    const shops = Array.isArray(data) ? data : [];
    const allItems = shops.flatMap((s: any) =>
      (Array.isArray(s?.shopCartItemDiscounts) ? s.shopCartItemDiscounts : [])
        .flatMap((g: any) => (Array.isArray(g?.shopCartItems) ? g.shopCartItems : []))
    );
    if (allItems.length) {
      const sumQty = allItems.reduce((sum: number, it: any) => {
        const n = Number(it?.prodCount);
        return sum + (Number.isFinite(n) ? n : 1);
      }, 0);
      const count = sumQty > 0 ? sumQty : allItems.length;
      cartBadgeCount.value = count;
      try { localStorage.setItem("cartBadgeCount", String(count)); } catch {}
      return;
    }
    const n = Number(
      (data as any)?.totalCount ?? (data as any)?.itemCount ?? (data as any)?.cartNum ?? (data as any)?.num ?? (data as any)?.count ?? 0
    );
    if (Number.isFinite(n) && n >= 0) {
      cartBadgeCount.value = n;
      try { localStorage.setItem("cartBadgeCount", String(n)); } catch {}
      return;
    }
    const items = Array.isArray((data as any)?.items)
      ? (data as any).items
      : Array.isArray((data as any)?.list)
      ? (data as any).list
      : Array.isArray((data as any)?.cartItems)
      ? (data as any).cartItems
      : [];
    cartBadgeCount.value = items.length;
    try { localStorage.setItem("cartBadgeCount", String(items.length)); } catch {}
  } catch {
    // keep previous count on error
  }
};
const fetchCategories = async () => {
  try {
    const r = await api.get<any>("/category/categoryInfo", undefined, { server: false, key: "nav-categoryInfo", retry: 0 });
    const data = (r as any)?.data ?? (r as any)?.raw;
    const arr = Array.isArray((data as any)?.records)
      ? (data as any).records
      : Array.isArray((data as any)?.list)
      ? (data as any).list
      : Array.isArray(data)
      ? (data as any)
      : [];
    const cats = arr.map((x: any) => ({
      name: x?.categoryName ?? x?.name,
      id: x?.categoryId ?? x?.id,
      categories: Array.isArray(x?.categories) ? x.categories : [],
    }))
    .filter((c: any) => c.name && c.id);
    if (r.ok && cats.length) dynamicCategories.value = cats;
  } catch {}
};
onMounted(() => {
  if (!props.overlay) return;
  const onScroll = () => {
    isSolid.value = window.scrollY > 40;
  };
  onScroll();
  window.addEventListener("scroll", onScroll);
});
onMounted(fetchCategories);
onMounted(() => {
  cartBadgeCount.value = 0;
  // 获取用户信息
  getUserInfo();
  if (isLoggedIn()) {
    fetchCartCount();
  }
  
  // 监听用户登录事件
  const onUserLoggedIn = () => {
    getUserInfo();
    fetchCartCount();
  };
  
  // 监听购物车变化事件
  const onCartChanged = () => {
    if (isLoggedIn()) {
      fetchCartCount();
    }
  };
  
  window.addEventListener("user-logged-in", onUserLoggedIn);
  window.addEventListener("cart-changed", onCartChanged);
  
  onUnmounted(() => {
    window.removeEventListener("user-logged-in", onUserLoggedIn);
    window.removeEventListener("cart-changed", onCartChanged);
  });
});
</script>

<style scoped>
.nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 20;
  height: 0.8rem;
  display: flex;
  align-items: center;
  background: transparent;
  transition: background 0.25s, box-shadow 0.25s;
  font-size: 0.16rem;
  padding: 0 0.6rem 0 0.2rem;
  border-bottom: 0.01rem solid #DFE0E0;
}
.nav.solid {
  background: #fff;
  border-bottom: 0.01rem solid #DFE0E0;
}
.nav-inner {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.right {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}
.brand {
  display: flex;
  align-items: center;
}
.brand a { display: inline-flex; align-items: center; }
.brand img {
  width: 2rem;
}
.menu {
  list-style: none;
  display: flex;
  gap: 0.5rem;
  height: 0.8rem;
  line-height: 0.8rem;
}
.menu-item { position: relative; }
.menu-item a {
  color: #121212;
  font-size: 0.18rem;
  padding: 0.1rem 0;
  position: relative;
}
.menu-item a::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: -0.01rem;
  height: 0.01rem;
  background: transparent;
}
.menu-item a:hover::after {
  background: #121212;
}
.submenu {
  display: none;
  position: absolute;
  top: 99%;
  left: 0;
  background: #fff;
  padding: 0.2rem 0;
  min-width: 1.2rem;
  line-height: 0.18rem;
  text-align: center;
  z-index: 30;
}
.has-submenu:hover .submenu { display: block; }
.submenu-item { white-space: nowrap; }
.submenu-item a {
  color: #939597;
  display: block;
  padding: 0.12rem 0;
  font-size: 0.16rem;
}

.submenu-item a::after {
  content: "";
  display: none;
}
.submenu-item a:hover {
  color: #121212;
  background: #f5f5f5;
}
.actions {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  color: #222;
}
.actions :deep(.el-icon) {
  font-size: 0.22rem;
}
.actions .iconfont {
  font-size: 0.22rem;
}
.clickable {
  cursor: pointer;
}
/* 用户信息样式 */
.user-info {
  display: flex;
  align-items: center;
  gap: 0.15rem;
  padding: 0.05rem 0;
}
.user-avatar {
  width: 0.36rem;
  height: 0.36rem;
  border-radius: 50%;
  object-fit: cover;
}
.user-nickname {
  font-size: 0.16rem;
  color: #121212;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 1rem;
}
.cart {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.badge {
  position: absolute;
  bottom: -0.06rem;
  right: -0.12rem;
  background: #000;
  color: #fff;
  font-size: 0.12rem;
  width: 0.2rem;
  height: 0.2rem;
  line-height: 0.22rem;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.search-wrap {
  position: fixed;
  top: 0.8rem;
  left: 0;
  right: 0;
  z-index: 21;
  background: #fff;
  border: 0.01rem solid #DFE0E0;
}
.search-inner {
  margin: 0 auto;
  padding: 0.2rem 0.5rem;
}
.s-input :deep(.el-input__wrapper) {
  background: #f7f7f7;
  box-shadow: none;
  height: 0.88rem;
  padding: 0 0.5rem 0 0.35rem;
}
.s-input :deep(.el-input__inner) {
  height: 0.88rem;
  line-height: 0.88rem;
  font-size: 0.24rem;
  padding-left: 0.4rem;
}
.s-input :deep(.el-input__prefix),
.s-input :deep(.el-input__suffix) {
  height: 100%;
  display: flex;
  align-items: center;
}
.s-input :deep(.el-icon) { font-size: 0.24rem; }
.s-input :deep(.s-prefix) { color: #333537; font-size: 0.24rem; }
.s-input :deep(.s-suffix) { color: #121212; }
.suggest {
  list-style: none;
}
.suggest li {
  padding: 0.2rem 0.5rem;
  cursor: pointer;
  font-size: 0.18rem;
  line-height: 1.6;
  color: #999;
}
.suggest .hl {
  color: #222;
}
.suggest li:hover {
  background: #f2f2f2;
}
.search-mask {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0.8rem;
  z-index: 10;
  background: rgba(0, 0, 0, 0.5);
}
</style>